/* src/components/Navbar/NotificationBell.jsx */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import useNotifications from '../Hooks/useNotifications';
import NotificationIcon from './NotificationIcon'; 

// --- Helper: Time-Ago Function ---
const formatTimeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const seconds = Math.floor((now - new Date(date)) / 1000);

  let interval = seconds / 31536000; // Years
  if (interval > 1) return Math.floor(interval) + "y ago";
  interval = seconds / 2592000; // Months
  if (interval > 1) return Math.floor(interval) + "mo ago";
  interval = seconds / 86400; // Days
  if (interval > 1) return Math.floor(interval) + "d ago";
  interval = seconds / 3600; // Hours
  if (interval > 1) return Math.floor(interval) + "h ago";
  interval = seconds / 60; // Minutes
  if (interval > 1) return Math.floor(interval) + "m ago";
  return Math.floor(seconds) + "s ago";
};


// --- The Main Component ---
const NotificationBell = () => {
  // ... (All logic from line 100 to 229 remains unchanged) ...
  const { notifications, unreadCount, markAllAsRead, markOneAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('unread');
  const dropdownRef = useRef(null); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);


  const onOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (notifId) => {
    if (notifications.find(n => n._id === notifId)?.isRead === false) {
      markOneAsRead(notifId);
    }
    setIsOpen(false); 
  };
  
  const filteredNotifications = notifications.filter(n => 
    filter === 'all' ? true : !n.isRead
  );
  const limitedNotifications = filteredNotifications.slice(0, 4);

  return (
    <div className="relative z-50" ref={dropdownRef}>
      
      {/* The Bell Icon Button */}
      <button 
        onClick={onOpen}
        // NOTE: Your original bell was 'text-white'. You may need to change this
        // if your navbar isn't dark. I'll leave it as-is for now.
        className={`relative p-1 text-xl transition-colors md:text-2xl ${isOpen ? 'text-cyan-300' : 'text-white hover:text-cyan-300'}`}
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center transform translate-x-1/2 -translate-y-1/2">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* --- Backdrop Portal --- */}
      {isOpen && createPortal(
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
          aria-hidden="true"
        ></div>,
        document.body
      )}

      {/* The Dropdown Panel */}
      {isOpen && (
        <div 
          className="absolute right-0 z-50 overflow-hidden bg-white border border-gray-200 shadow-xl 
                     mt-4
                     w-[28rem]
                     max-h-[36rem]
                     rounded-xl"
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900 text-lg">Notifications</h3>
            
            {/* Filter Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  filter === 'unread' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Unread ({unreadCount})
              </button>
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  filter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All
              </button>
            </div>
          </div>
          
          {/* Notification List (LIMITED to 4) */}
          <div className="divide-y divide-gray-100 overflow-y-auto max-h-[calc(36rem-130px)]"> 
            {limitedNotifications.length > 0 ? (
              limitedNotifications.map(notif => ( 
                <Link
                  key={notif._id}
                  to={notif.link}
                  onClick={() => handleNotificationClick(notif._id)}
                  className="block p-4 transition-colors duration-150 hover:bg-gray-100"
                >
                  <div className="flex gap-3">
                    <div>
                      {/* 3. IT'S BEING USED HERE, nice and clean. */}
                      <NotificationIcon type={notif.type} />
                    </div>
                    
                    <div className="flex-1 overflow-hidden">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-800 capitalize truncate">
                          {notif.type?.replace('_', ' ') || 'System'}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <span className="text-xs text-gray-500">
                            {formatTimeAgo(notif.createdAt)}
                          </span>
                          {!notif.isRead && (
                            <div className="w-2.5 h-2.5 bg-gray-900 rounded-full" title="Unread"></div>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 leading-snug line-clamp-2">
                        {notif.message}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="p-6 text-sm text-center text-gray-500">
                {filter === 'unread' ? "No unread notifications." : "No notifications yet."}
              </p>
            )}
          </div>
          
          {/* Footer Link to All Notifications */}
          <Link 
            to="/notifications" 
            onClick={() => setIsOpen(false)}
            className="block text-center p-3 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-blue-600 rounded-b-lg border-t border-gray-200"
          >
            View All Notifications
          </Link>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;